import { CurrencyPipe, NgFor, NgIf } from '@angular/common';
import { Component, ElementRef, ViewChild, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { PublicCatalogItem } from 'src/app/models/catalogs/public-catalog';
import { UserRole } from 'src/app/models/security/user-role-enum';
import { DropDownKeywords, DropdownOption } from 'src/app/models/shared/dropdown-option';
import { AuthService } from 'src/app/services/auth.service';
import { DropdownService } from 'src/app/services/dropdown.service';
import { PublicCatalogService } from 'src/app/services/public-catalog.service';
import { FormsModule } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { PublicCatalogDetailComponent } from './public-catalog-detail/public-catalog-detail.component';

@Component({
  selector: 'app-public-catalog',
  imports: [NgFor, FormsModule, NgIf, CurrencyPipe, PublicCatalogDetailComponent],
  templateUrl: './public-catalog.component.html',
  styleUrl: './public-catalog.component.css'
})
export class PublicCatalogComponent {
  private readonly dropdownService = inject(DropdownService);
  private readonly authService = inject(AuthService);
  private readonly catalogService = inject(PublicCatalogService);

  items: PublicCatalogItem[] = [];
  selectedItem: PublicCatalogItem | null = null;

  eligibility: string = '';
  functionalType?: number;

  page = 1;
  pageSize = 20;
  totalItems = 0;
  loading = false;
  finished = false;

  private observer?: IntersectionObserver;
  @ViewChild('infiniteAnchor', { static: false }) infiniteAnchor?: ElementRef<HTMLElement>;

  private activeSub?: Subscription;
  private currentQueryKey = '';
  fileBaseUrl = `${environment.api.url}/File/ViewFile`;

  functionalTypeOptions: DropdownOption[] = [];

  isLoggedIn = this.authService.isLoggedIn();
  roleId = this.authService.getUserRoleId();
  userRole = UserRole;

  ngOnInit(): void {
    this.dropdownService.GetOptions(DropDownKeywords.FunctionalType).subscribe({
      next: (res) => (this.functionalTypeOptions = res),
    });
    this.resetAndLoad();
  }

  ngAfterViewInit(): void {
    this.observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          this.loadNextPage();
        }
      },
      {
        root: null,
        threshold: 0,
        rootMargin: '400px'
      }
    );

    if (this.infiniteAnchor?.nativeElement) {
      this.observer.observe(this.infiniteAnchor.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.activeSub?.unsubscribe();
    this.observer?.disconnect();
  }

  onFiltersChanged(): void {
    this.resetAndLoad();
  }

  private resetAndLoad(): void {
    this.activeSub?.unsubscribe();
    this.items = [];
    this.page = 1;
    this.totalItems = 0;
    this.finished = false;
    this.loading = false;
    this.currentQueryKey = this.buildQueryKey();
    this.loadNextPage();
  }

  private buildQueryKey(): string {
    return JSON.stringify({
      pageSize: this.pageSize,
      eligibility: this.eligibility || null,
      functionalType: this.functionalType ?? null
    });
  }

  loadNextPage(): void {
    if (this.loading || this.finished) return;

    this.loading = true;
    const queryKeyAtRequest = this.currentQueryKey;

    this.activeSub = this.catalogService
      .getPublicCatalog(this.page, this.pageSize, this.eligibility || undefined, this.functionalType)
      .subscribe({
        next: res => {
          if (queryKeyAtRequest !== this.currentQueryKey) {
            this.loading = false;
            return;
          }

          this.totalItems = res.totalItems ?? this.totalItems;
          const received = res.items || [];

          const existingIds = new Set(this.items.map(i => i.id));
          const merged = received.filter(i => !existingIds.has(i.id));
          this.items = this.items.concat(merged);

          this.page++;

          if (merged.length < this.pageSize || this.items.length >= this.totalItems) {
            this.finished = true;
          }

          this.loading = false;
        },
        error: _ => {
          this.loading = false;
        }
      });
  }

  viewDetail(item: PublicCatalogItem): void {
    this.selectedItem = item;
    const modalEl = document.getElementById('catalogDetail');
    if (!modalEl) return;

    const modal = new (window as any).bootstrap.Modal(modalEl);
    modal.show();
  }

  applyFilters(): void {
    this.resetAndLoad();
  }

  clearFilters(): void {
    this.eligibility = '';
    this.functionalType = undefined;
    this.resetAndLoad();
  }
}